<template>
  <StandardCardPageContainer :include-navigation="true">
    <template #title>Create customer</template>
    <div>
      <v-row>
        <v-col>
          <h3 class="text-left">Enter user information</h3>
        </v-col>
      </v-row>
      <div class="text-left">
        <v-row>
          <v-col>
            <PInputField
              :value="state.user.email"
              label="Email"
              :error="v.user.email.$error"
              :error-messages="v.user.email.$errors[0]?.$message.toString()"
              @update-event="(val: string) => (state.user.email = val)"
            />
          </v-col>
          <v-col>
            <p>Role</p>
            <PAutocomplete
              :select-text="state.user.role"
              :items="roles"
              :error="v.user.role.$error"
              :error-messages="v.user.role.$errors[0]?.$message.toString()"
              @field-updated="(val: string) => (state.user.role = val)"
            />
          </v-col>
          <v-col>
            <PInputField
              :value="state.user.name"
              label="Name (optional)"
              @update-event="(val: string) => (state.user.name = val)"
            />
          </v-col>
        </v-row>
      </div>

      <v-row>
        <v-col>
          <h3 class="text-left">Enter company information</h3>
        </v-col>
      </v-row>
      <div class="text-left">
        <v-row>
          <v-col>
            <PInputField
              :value="state.trustGroupDto.company.companyName"
              label="Company Name"
              :error="v.trustGroupDto.company.companyName.$error"
              :error-messages="
                v.trustGroupDto.company.companyName.$errors[0]?.$message.toString()
              "
              @update-event="(val: string) => (state.trustGroupDto.company.companyName = val)"
            />
          </v-col>
          <v-col>
            <PInputField
              :value="state.trustGroupDto.company.organisationNumber"
              label="Organisation number"
              :error="v.trustGroupDto.company.organisationNumber.$error"
              :error-messages="
                v.trustGroupDto.company.organisationNumber.$errors[0]?.$message.toString()
              "
              @update-event="(val: string) => (state.trustGroupDto.company.organisationNumber = val)"
            />
          </v-col>
          <v-col>
            <p>Operating Country</p>
            <PAutocomplete
              :items="
                itemsStore.countries.map((c) => ({
                  id: c.code,
                  text: c.name,
                }))
              "
              :select-text="state.trustGroupDto.company.operatingCountryCode"
              :error="v.trustGroupDto.company.operatingCountryCode.$error"
              :error-messages="
                v.trustGroupDto.company.operatingCountryCode.$errors[0]?.$message.toString()
              "
              @field-updated="(val: string) => (state.trustGroupDto.company.operatingCountryCode = val)"
            />
          </v-col>
        </v-row>
      </div>

      <v-row>
        <v-col>
          <h3 class="text-left">Enter customer type</h3>
        </v-col>
      </v-row>
      <div class="text-left">
        <v-row>
          <v-col cols="4">
            <p>Trust Group Type</p>
            <PAutocomplete
              :items="trustGroupTypes"
              :select-text="state.trustGroupDto.trustGroupType"
              :error="v.trustGroupDto.trustGroupType.$error"
              :error-messages="
                v.trustGroupDto.trustGroupType.$errors[0]?.$message.toString()
              "
              @field-updated="(val: string) => (state.trustGroupDto.trustGroupType = val as TrustGroupType)"
            />
          </v-col>
        </v-row>
      </div>
    </div>

    <template #nextButton>
      <PPrimaryButton class="mt-3 mb-5 mr-3" @click="createCustomer"
        >Create customer</PPrimaryButton
      ></template
    >
  </StandardCardPageContainer>
</template>

<script setup lang="ts">
import StandardCardPageContainer from "@/components/containers/StandardCardPageContainer.vue";
import PInputField from "@/components/custom/PInputField.vue";
import PAutocomplete from "@/components/custom/PAutocomplete.vue";
import { UserProfile } from "@/models/userProfile";
import { ref } from "vue";
import { email, required } from "@vuelidate/validators";
import { UserProfileType } from "@/models/enum/userProfileType";
import PPrimaryButton from "@/components/custom/buttons/PPrimaryButton.vue";
import useVuelidate from "@vuelidate/core";
import { useItemsStore } from "@/stores/itemsStore";
import { createTrustGroup } from "@/services/trustGroupAdminService";
import { createUserProfile } from "@/services/userProfileAdminService";
import { useSnackBarStore } from "@/stores/snackBarStore";
import router from "@/router";
import { RouterPath } from "@/models/enum/routerPath";
import { TrustGroupType } from "@/models/enum/trustGroupType";
import { TrustGroupDto } from "@/models/dto/trustGroupDto";

const itemsStore = useItemsStore();
const snackbarStore = useSnackBarStore();
const trustGroupDto = ref(new TrustGroupDto());
const user = ref(new UserProfile());
const trustGroupTypes = new Array<string>(
  TrustGroupType.Channel1Customer,
  TrustGroupType.Channel2Customer
);
const roles = new Array<string>(
  UserProfileType.User,
  UserProfileType.Administrator
);

const state = ref({ trustGroupDto, user });

const rules = {
  trustGroupDto: {
    company: {
      companyName: { required },
      organisationNumber: { required },
      operatingCountryCode: { required },
    },
    trustGroupType: { required },
  },
  user: {
    email: { required, email },
    role: { required },
  },
};

const v = useVuelidate(rules, state);

async function createCustomer() {
  const validForm = await v.value.$validate();
  if (!validForm) {
    return;
  }
  try {
    state.value.user.email.toLowerCase();
    state.value.trustGroupDto.userProfileEmails.push(state.value.user.email);
    await createTrustGroup(state.value.trustGroupDto);
    await createUserProfile(state.value.user);
  } catch (error) {
    snackbarStore.showError("Creation failed");
  }
  snackbarStore.showSuccess("Customer created");
  router.push(RouterPath.AdministratorPage);
}
</script>
